import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy } from 'lucide-react';

const getRankStyle = (rank) => {
  if (rank === 1) return 'rank-gold bg-gradient-to-r from-yellow-50 to-yellow-100/50 dark:from-yellow-900/20 dark:to-yellow-800/10';
  if (rank === 2) return 'rank-silver bg-slate-50 dark:bg-slate-800/50';
  if (rank === 3) return 'rank-bronze bg-orange-50 dark:bg-orange-900/20';
  return 'bg-card';
};

const getRankBadgeStyle = (rank) => {
  if (rank === 1) return 'rank-1';
  if (rank === 2) return 'rank-2';
  if (rank === 3) return 'rank-3';
  return 'rank-other';
};

const getInitials = (name) => {
  if (!name) return '??';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const LeaderboardCard = ({ rank, teamName, points, owner, partner, isDuo, isCurrentUser }) => {
  return (
    <div
      className={cn(
        'flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 hover-lift',
        getRankStyle(rank),
        isCurrentUser && 'ring-2 ring-primary ring-offset-2'
      )}
      data-testid={`leaderboard-card-${rank}`}
    >
      {/* Rank Badge */}
      <div className={cn('rank-badge flex-shrink-0', getRankBadgeStyle(rank))}>
        {rank <= 3 ? <Trophy className="w-4 h-4" /> : rank}
      </div>

      {/* Team Info */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="flex -space-x-2">
          <Avatar className="w-10 h-10 border-2 border-background">
            <AvatarImage src={owner?.avatar_url} alt={owner?.name} />
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {getInitials(owner?.name)}
            </AvatarFallback>
          </Avatar>
          {isDuo && partner && (
            <Avatar className="w-10 h-10 border-2 border-background">
              <AvatarImage src={partner?.avatar_url} alt={partner?.name} />
              <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
                {getInitials(partner?.name)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{teamName}</p>
          <p className="text-xs text-muted-foreground truncate">
            {owner?.name}
            {isDuo && partner && ` & ${partner.name}`}
          </p>
        </div>
      </div>

      {/* Points */}
      <div className="text-right flex-shrink-0">
        <p
          className={cn(
            'text-lg font-bold',
            points > 0 ? 'score-positive' : points < 0 ? 'score-negative' : ''
          )}
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {points > 0 ? '+' : ''}{points}
        </p>
        <p className="text-xs text-muted-foreground">punti</p>
      </div>
    </div>
  );
};
